import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SafetyPolicyType } from "@unbox/api";
import { client } from "@/api/client";
import { handleError } from "@/api/error";

// Workspace default safety template shapes, sourced from the API (UNB-1946).
export type { WorkspaceRiskCatalogItem, WorkspaceRiskListItem } from "@unbox/api";

const workspaceRisksKey = (workspaceId: string) => ["workspace-risks", workspaceId];
const workspaceRiskCatalogKey = (workspaceId: string) => ["workspace-risk-catalog", workspaceId];
const workspaceDomainKnowledgeKey = (workspaceId: string) => ["workspace-domain-knowledge", workspaceId];

// The catalog of risks the workspace can configure as defaults: every global
// (non-custom) risk, each with the workspace's effective tier. Pair with
// `useWorkspaceRisksList` to determine which catalog entries are enabled.
export const useWorkspaceRiskCatalog = (workspaceId: string) =>
  useQuery({
    queryKey: workspaceRiskCatalogKey(workspaceId),
    queryFn: async () => {
      const res = await client.workspace[":workspaceId"].risks.catalog.$get({ param: { workspaceId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!workspaceId,
  });

export const useWorkspaceRisksList = (workspaceId: string) =>
  useQuery({
    queryKey: workspaceRisksKey(workspaceId),
    queryFn: async () => {
      const res = await client.workspace[":workspaceId"].risks.$get({ param: { workspaceId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!workspaceId,
  });

// The workspace's domain knowledge map (placeholder keyword lists stored as JSON
// array strings). Used by the config editor to prefill placeholder inputs.
export const useWorkspaceDomainKnowledge = (workspaceId: string) =>
  useQuery({
    queryKey: workspaceDomainKnowledgeKey(workspaceId),
    queryFn: async () => {
      const res = await client.workspace[":workspaceId"].risks["domain-knowledge"].$get({ param: { workspaceId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!workspaceId,
  });

// A single risk's entry in the workspace default template. Keyed by
// safety_policy_type (the workspace has no concrete safety_policies rows).
type WorkspaceSafetyRiskEntry = {
  riskId: string;
  tier?: number;
  autoConfirm?: boolean;
  safetyPolicyType?: SafetyPolicyType | null;
  config?: Record<string, string[]>;
};

export const useSaveWorkspaceSafetyConfig = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      enabled: WorkspaceSafetyRiskEntry[];
      updated: WorkspaceSafetyRiskEntry[];
      disabled: string[];
      domainKnowledge?: Record<string, string>;
    }) => {
      const res = await client.workspace[":workspaceId"].risks["safety-config"].$post({
        param: { workspaceId },
        json: input,
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceRisksKey(workspaceId) });
      queryClient.invalidateQueries({ queryKey: workspaceRiskCatalogKey(workspaceId) });
      queryClient.invalidateQueries({ queryKey: workspaceDomainKnowledgeKey(workspaceId) });
    },
  });
};
